// PatientDiets.js
import React, { useState } from 'react';
import './KitchenDashboard.css';
import { useKitchenData } from './Context/KitchenDataContext';
import KitchenSidebar from './KitchenSidebar';

const PatientDiets = () => {
  const { patients, dietPlans, getScheduleForDate, menuItems } = useKitchenData();
  const [expandedIds, setExpandedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const today = new Date().toISOString().split('T')[0];
  const todaySchedule = getScheduleForDate(today);

  const toggle = (id) => {
    setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const isExpanded = (id) => expandedIds.includes(id);

  // Filter patients based on search
  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.room.includes(searchTerm) ||
    patient.condition.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.allergies.some(allergy => allergy.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Enhanced patient data with diet plan info
  const enhancedPatients = filteredPatients.map(patient => {
    const patientDietPlans = dietPlans.filter(plan => plan.patientId === patient.id);
    const activePlan = patientDietPlans.find(plan => {
      const startDate = new Date(plan.startDate);
      const endDate = new Date(plan.endDate);
      const currentDate = new Date(today);
      return currentDate >= startDate && currentDate <= endDate;
    });
    
    const patientSchedule = todaySchedule[patient.id] || {};
    const scheduleTimes = ['08:00', '10:30', '13:00', '16:00', '20:00'];
    const timeLabels = ['08:00', '10:30', '01:00pm', '4:00pm', '8:00pm'];
    const todayMeals = scheduleTimes.map((time, index) => ({
      time,
      displayTime: timeLabels[index],
      foodCode: patientSchedule[time] || '',
      menuItem: menuItems[patientSchedule[time]] || null
    })).filter(meal => meal.foodCode);

    return {
      ...patient,
      activePlan,
      dietPlansCount: patientDietPlans.length,
      todayMeals
    };
  });

  return (
    <div className="kitchen-layout">
      <KitchenSidebar />
      <div className="kitchen-root col-10">
        <div className="kitchen-header" style={{ marginBottom: 8 }}>
          <div>
            <h1 className="kh-title">Patient Diets</h1>
            <div className="kh-date">
              Manage dietary requirements for {patients.length} patients
            </div>
          </div>
        </div>

        <div className="kh-panel" style={{ marginBottom: 16 }}>
          <div style={{ padding: 12 }}>
            <input 
              className="kh-search" 
              placeholder="Search patients by name, room, diet plan or allergies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {enhancedPatients.map(patient => (
          <div key={patient.id} className="pd-card">
            <div className="pd-row pd-header">
              <div className="pd-name">{patient.name}</div>
              <div className="pd-chip">Room {patient.room}</div>
              <div className="pd-spacer" />
              
              {patient.activePlan && (
                <div className="pd-badge" style={{ backgroundColor: '#28a745' }} title="Active Diet Plan">
                  Active Plan
                </div>
              )}
              
              {patient.allergies && patient.allergies.length > 0 && (
                <div className="pd-badge" title="Allergies">
                  Allergies
                  <div className="pd-tooltip">
                    <ul>
                      {patient.allergies.map(a => (<li key={a}>{a}</li>))}
                    </ul>
                  </div>
                </div>
              )}
              
              <button 
                className={`pd-toggle ${isExpanded(patient.id) ? 'open' : ''}`} 
                onClick={() => toggle(patient.id)} 
                aria-label="Toggle details"
              >
                ▾
              </button>
            </div>
            
            <div className="pd-plan">
              {patient.activePlan ? patient.activePlan.notes || 'Active diet plan' : 'No active diet plan'}
            </div>

            {isExpanded(patient.id) && (
              <>
                <div className="pd-grid">
                  <div className="pd-box">
                    <div className="pd-box-title">Patient Info</div>
                    <div><strong>Age:</strong> {patient.age}</div>
                    <div><strong>Condition:</strong> {patient.condition}</div>
                    <div className="pd-muted" style={{ marginTop: 8 }}>Diet Plans:</div>
                    <div className="pd-chip muted">{patient.dietPlansCount} total</div>
                  </div>
                  
                  <div className="pd-box">
                    <div className="pd-box-title">Active Plan Details</div>
                    {patient.activePlan ? (
                      <>
                        <div><strong>Duration:</strong> {patient.activePlan.startDate} to {patient.activePlan.endDate}</div>
                        <div><strong>Meals Planned:</strong> {patient.activePlan.meals.length}</div>
                        {patient.activePlan.notes && (
                          <div style={{ marginTop: 8 }}>
                            <strong>Notes:</strong><br />
                            {patient.activePlan.notes}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="pd-muted">No active diet plan found</div>
                    )}
                  </div>
                  
                  <div className="pd-box">
                    <div className="pd-box-title">Allergies & Restrictions</div>
                    {patient.allergies.length > 0 ? (
                      <ul className="pd-list">
                        {patient.allergies.map(allergy => (
                          <li key={allergy} style={{ color: '#dc3545' }}>{allergy}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="pd-muted">No known allergies</div>
                    )}
                  </div>
                </div>

                {/* Today's Meals Schedule */}
                <div className="pd-meals">
                  <div className="pd-box-title" style={{ marginBottom: 12 }}>
                    Today's Meal Schedule ({patient.todayMeals.length} meals)
                  </div>
                  
                  {patient.todayMeals.length > 0 ? (
                    <>
                      <div className="pd-meals-head">
                        {patient.todayMeals.map(meal => (
                          <div key={meal.time}>{meal.displayTime}</div>
                        ))}
                      </div>
                      <div className="pd-meals-row">
                        {patient.todayMeals.map(meal => (
                          <div key={meal.time}>
                            <div className="pd-meal-item">
                              {meal.menuItem ? meal.menuItem.name : 'Unknown'}
                            </div>
                            <div className="pd-meal-code">{meal.foodCode}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="pd-muted">No meals scheduled for today</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        {enhancedPatients.length === 0 && searchTerm && (
          <div className="kh-panel">
            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              <h3>No patients found</h3>
              <p>No patients match your search criteria "{searchTerm}"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientDiets;